import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import translations from './userprofile.i18n';
import * as css from './userprofile.module.scss';
import PanelHeader from '../components/panelheader/panelHeader';
import {
  BrukerrollerDto,
  MuligPraksisstedDto,
  PraksisstedbrukerprofilPutTilgangerInnerDto as PsPraksisstedbrukerprofilPutTilgangerDto,
} from '../api/generated/praksissted-generated-api';
import { useLoader } from '../api/useLoader';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import {
  BrukerrollerPSDto,
  HttpError,
  PraksisstedbrukerprofilPutTilgangerInnerDto as UhPraksisstedbrukerprofilPutTilgangerDto,
  PraksisstedDto,
} from '../api/generated/studiested-generated-api';
import { Bruker } from './praksisstedUserprofile';
import { IndentMuligPraksissted } from '../components/indentation/indentation';
import Autocomplete from '../components/input/autocomplete';
import Dropdown from '../components/input/dropdown';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import TilgangColumns from './tilgangColumns';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { PsaUserContext } from '../user/PsaUserStore';

type Brukerroller = BrukerrollerDto | BrukerrollerPSDto;

interface Praksissted {
  id: string;
  navn: string;
  dybde?: number;
  deaktivert?: boolean;
}

interface PraksisstedTilgang {
  brukerrolle: Brukerroller;
  kanRedigere: boolean;
  praksissted: Praksissted;
}

interface PraksisstedbrukerprofilPutTilganger {
  praksisstedId: string;
  tilgangsrolle: Brukerroller;
}

export interface ProfilData {
  bruker: Bruker;
  tilganger?: PraksisstedTilgang[];
}

const Tilganger: React.FC<
  React.PropsWithChildren<{
    brukerprofil: ProfilData;
    canAdminister: boolean;
    reloadBrukerprofil?: () => void;
    isSelf?: boolean;
  }>
> = ({ brukerprofil, canAdminister, reloadBrukerprofil, isSelf }) => {
  const { formatMessage } = useIntl();
  const { praksisstedApi: psPraksisstedApi } = useContext(
    PraksisstedApiContext,
  );
  const { praksisstedApi: uhPraksisstedApi } = useContext(StudiestedApiContext);
  const { psaUser } = useContext(PsaUserContext);
  const isPsaUser = !!psaUser;

  const { addNotification } = useContext(NotificationFunctionContext);
  const tilganger: PraksisstedTilgang[] = brukerprofil.tilganger || [];
  const [endreTilgang, setEndreTilgang] = useState<
    PraksisstedTilgang | undefined
  >();
  const [nyTilgang, setNyTilgang] = useState<boolean>(false);

  const labels = {
    tilganger: formatMessage({ id: translations.tilganger.id }),
    tilgangerSubtitle: formatMessage({ id: translations.tilgangerSubtitle.id }),
    tilgangLagret: formatMessage({ id: translations.tilgangLagret.id }),
    tilgangSlettet: formatMessage({ id: translations.tilgangSlettet.id }),
  };

  const saveTilgang = (
    selectedAddPraksisstedId: string,
    selectedAddRolle: Brukerroller,
    editTilgang?: PraksisstedTilgang,
  ) => {
    const tilgangerToSave = (tilganger || [])
      .filter((tilgang) => tilgang !== editTilgang)
      .map((tilgang) => {
        return {
          praksisstedId: tilgang.praksissted.id,
          tilgangsrolle: tilgang.brukerrolle,
        };
      });
    tilgangerToSave.push({
      praksisstedId: selectedAddPraksisstedId,
      tilgangsrolle: selectedAddRolle,
    });
    save(tilgangerToSave, labels.tilgangLagret);
  };

  const deleteTilgang = (deletedTilgang: PraksisstedTilgang) => {
    const allExceptDeleted = (brukerprofil.tilganger || [])
      .filter((tilgang) => tilgang !== deletedTilgang)
      .map((tilgang) => {
        return {
          praksisstedId: tilgang.praksissted.id,
          tilgangsrolle: tilgang.brukerrolle,
        };
      });
    save(allExceptDeleted, labels.tilgangSlettet);
  };

  function save(
    tilgangerToSave: PraksisstedbrukerprofilPutTilganger[],
    saveMessage: string,
  ) {
    (isPsaUser
      ? psPraksisstedApi.praksisstedbrukerprofilIdPut({
          pathParams: { id: brukerprofil.bruker.id },
          praksisstedbrukerprofilPutDto: {
            tilganger:
              tilgangerToSave as PsPraksisstedbrukerprofilPutTilgangerDto[],
          },
        })
      : uhPraksisstedApi.praksisstedbrukerprofilIdPut({
          pathParams: { id: brukerprofil.bruker.id },
          praksisstedbrukerprofilPutDto: {
            tilganger:
              tilgangerToSave as UhPraksisstedbrukerprofilPutTilgangerDto[],
          },
        })
    )
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: saveMessage,
        });
        reloadBrukerprofil!();
      })
      .catch((err: HttpError) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        }),
      );
  }

  return (
    <div>
      <div className={css.privileges}>
        <PanelHeader
          title={labels.tilganger}
          subtitle={canAdminister ? labels.tilgangerSubtitle : ''}
          className={css.header}
        />

        <TilgangTable
          tilganger={tilganger || []}
          nyTilgang={nyTilgang}
          endreTilgang={endreTilgang}
          canAdminister={canAdminister}
          deleteTilgang={deleteTilgang}
          setEndreTilgang={setEndreTilgang}
          saveTilgang={saveTilgang}
          isSelf={isSelf}
          isPsaUser={isPsaUser}
          praksisstedTilgangId={
            brukerprofil.tilganger && brukerprofil.tilganger[0].praksissted.id
          }
        />

        {canAdminister && (
          <ActionButton
            endreTilgang={endreTilgang}
            setEndreTilgang={setEndreTilgang}
            nyTilgang={nyTilgang}
            setNyTilgang={setNyTilgang}
          />
        )}
      </div>
    </div>
  );
};

const ActionButton: React.FC<
  React.PropsWithChildren<{
    endreTilgang: PraksisstedTilgang | undefined;
    setEndreTilgang: (tilgang: PraksisstedTilgang | undefined) => void;
    nyTilgang: boolean;
    setNyTilgang: (newMode: boolean) => void;
  }>
> = ({ endreTilgang, setEndreTilgang, nyTilgang, setNyTilgang }) => {
  const { formatMessage } = useIntl();

  const labels = {
    nyTilgang: formatMessage({ id: translations.nyTilgang.id }),
    avbryt: formatMessage({ id: translations.avbryt.id }),
  };
  return (
    <div className={css.permissionButtonContainer}>
      {!endreTilgang && !nyTilgang && (
        <Button.Secondary onClick={() => setNyTilgang(true)}>
          {labels.nyTilgang}
        </Button.Secondary>
      )}

      {(endreTilgang || nyTilgang) && (
        <Button.Tertiary
          onClick={() => {
            setNyTilgang(false);
            setEndreTilgang(undefined);
          }}
        >
          {labels.avbryt}
        </Button.Tertiary>
      )}
    </div>
  );
};

const TilgangTable: React.FC<
  React.PropsWithChildren<{
    tilganger: PraksisstedTilgang[];
    endreTilgang: PraksisstedTilgang | undefined;
    nyTilgang: boolean;
    canAdminister: boolean;
    deleteTilgang: (tilgang: PraksisstedTilgang) => void;
    setEndreTilgang: (tilgang: PraksisstedTilgang) => void;
    saveTilgang: (
      selectedAddPraksisstedId: string,
      selectedAddRolle: Brukerroller,
      editTilgang?: PraksisstedTilgang,
    ) => void;
    isSelf?: boolean;
    isPsaUser: boolean;
    praksisstedTilgangId?: string;
  }>
> = ({
  tilganger,
  endreTilgang,
  nyTilgang,
  canAdminister,
  deleteTilgang,
  setEndreTilgang,
  saveTilgang,
  isSelf,
  isPsaUser,
  praksisstedTilgangId,
}) => {
  const { formatMessage, locale } = useIntl();

  const translateRolle = function (rolle: Brukerroller) {
    return formatMessage({ id: translations[`rolle${rolle}`].id });
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (col: number, a: PraksisstedTilgang, b: PraksisstedTilgang) => {
    switch (col) {
      case 0:
        return compare(a.praksissted.navn, b.praksissted.navn);
      case 1:
        return compare(
          translateRolle(a.brukerrolle),
          translateRolle(b.brukerrolle),
        );
    }
    return 0;
  };

  const sortedTilgangerByActiveCol = (col: number, ascending: boolean) =>
    [...tilganger]
      .filter((tilgang) => tilgang !== endreTilgang!!)
      .sort((a, b) => {
        return ascending ? sort(col, b, a) : sort(col, a, b);
      });

  const [activeCol, setActiveCol] = useState<number>(0);
  const [sortedTilganger, setSortedTilganger] = useState<PraksisstedTilgang[]>(
    sortedTilgangerByActiveCol(0, false),
  );

  const labels = {
    brukerrettighet: formatMessage({ id: translations.brukerrettighet.id }),
    tilgangsnivaa: formatMessage({ id: translations.tilgangsnivaa.id }),
    handling: formatMessage({ id: translations.handling.id }),
    endre: formatMessage({ id: translations.endre.id }),
    slett: formatMessage({ id: translations.slett.id }),
  };

  const headers = [
    {
      title: formatMessage({ id: translations.tilgangsnivaa.id }),
      type: TilgangColumns.STED,
    },
    {
      title: formatMessage({ id: translations.brukerrettighet.id }),
      type: TilgangColumns.ROLLE,
    },
    {
      title: formatMessage({ id: translations.handling.id }),
      type: TilgangColumns.HANDLING,
    },
  ];

  const getRows = () =>
    sortedTilganger.map((tilgang) => {
      let tabHeader = 0;

      return (
        <TableRow key={tilgang.praksissted.id}>
          <TableCellTd header={headers[tabHeader++].title}>
            <div className={css.stedNavnCell}>
              {tilgang.praksissted.navn}
              {(tilgang.kanRedigere ||
                isSelf ||
                (!isPsaUser && canAdminister)) && (
                <OnStedHoverCard
                  id={tilgang.praksissted.id}
                  navn={tilgang.praksissted.navn}
                  sted={StedType.PRAKSISSTED}
                />
              )}
            </div>
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {translateRolle(tilgang.brukerrolle)}
          </TableCellTd>
          {canAdminister && (
            <TableCellTd header={headers[tabHeader].title} align="right">
              {tilganger.length > 1 && (
                <Button.Secondary
                  disabled={!tilgang.kanRedigere}
                  onClick={() => deleteTilgang(tilgang)}
                >
                  {labels.slett}
                </Button.Secondary>
              )}
              {tilganger.length === 1 && (
                <Button.Secondary
                  disabled={!tilgang.kanRedigere}
                  onClick={() => setEndreTilgang(tilgang)}
                >
                  {labels.endre}
                </Button.Secondary>
              )}
            </TableCellTd>
          )}
        </TableRow>
      );
    });

  const sortTilganger = (col: number, ascending: boolean) =>
    setSortedTilganger(sortedTilgangerByActiveCol(col, ascending));

  const canSortIndices = [0, 1];

  const getHeaders = () =>
    headers.map(({ title, type }) => {
      if (type === TilgangColumns.HANDLING && !canAdminister)
        return <React.Fragment key={title} />;

      return (
        <TableCellTh
          key={title}
          colIndex={type}
          activeCol={type === activeCol}
          setActiveCol={setActiveCol}
          sort={sortTilganger}
          canSort={canSortIndices.includes(type)}
          align={type === TilgangColumns.HANDLING ? 'right' : 'inherit'}
        >
          {title}
        </TableCellTh>
      );
    });

  return (
    <Table>
      <TableHeader>
        <TableRow top>{getHeaders()}</TableRow>
      </TableHeader>
      <TableMain>
        {/* View existing tilganger */}
        {getRows()}
        {/* Add or edit tilgang */}
        {(endreTilgang || nyTilgang) && (
          <NewTilgang
            tilgang={endreTilgang}
            saveTilgang={saveTilgang}
            isPsaUser={isPsaUser}
            praksisstedTilgangId={praksisstedTilgangId}
          />
        )}
      </TableMain>
    </Table>
  );
};

const instanceOfPraksisstedDto = (
  praksissted: Praksissted | PraksisstedDto,
): praksissted is PraksisstedDto => {
  return 'level' in praksissted; // level does only exist in PraksisstedDto
};

const mapDataToPraksisstedList = (
  data: Praksissted[] | PraksisstedDto[] | undefined,
): Praksissted[] | undefined => {
  if (
    data &&
    data.some((value: Praksissted | PraksisstedDto) =>
      instanceOfPraksisstedDto(value),
    )
  ) {
    return (data as PraksisstedDto[]).map((praksissted) => ({
      id: praksissted.praksissted.id,
      navn: praksissted.praksissted.navn,
      dybde: praksissted.level,
      deaktivert: !praksissted.canInvite,
    }));
  }

  return data ? (data as Praksissted[]) : undefined;
};

const NewTilgang: React.FC<
  React.PropsWithChildren<{
    tilgang: PraksisstedTilgang | undefined;
    saveTilgang: (
      selectedAddPraksisstedId: string,
      selectedAddRolle: Brukerroller,
      editTilgang?: PraksisstedTilgang,
    ) => void;
    isPsaUser: boolean;
    praksisstedTilgangId?: string;
  }>
> = ({ tilgang, saveTilgang, isPsaUser, praksisstedTilgangId }) => {
  const [selectedAddPraksisstedId, setSelectedAddPraksisstedId] = useState('');
  const [selectedAddRolle, setSelectedAddRolle] = useState<Brukerroller | ''>(
    '',
  );

  useEffect(() => {
    setSelectedAddPraksisstedId((tilgang && tilgang.praksissted.id) || '');
    setSelectedAddRolle((tilgang && tilgang.brukerrolle) || '');
  }, [tilgang]);

  const { praksisstedApi: psPraksisstedApi } = useContext(
    PraksisstedApiContext,
  );
  const { praksisstedApi: uhPraksisstedApi } = useContext(StudiestedApiContext);

  const { data } = useLoader<Praksissted[] | PraksisstedDto[]>(
    () =>
      isPsaUser
        ? psPraksisstedApi.administrertePraksistederGet({})
        : uhPraksisstedApi.praksisstederGet({
            queryParams: { praksisstedId: praksisstedTilgangId || '' },
          }),
    [],
  );

  const praksissteder = mapDataToPraksisstedList(data);

  const save = () => {
    saveTilgang(
      selectedAddPraksisstedId,
      selectedAddRolle as Brukerroller,
      tilgang,
    );
  };

  const { formatMessage } = useIntl();
  const labels = {
    lagre: formatMessage({ id: translations.lagre.id }),
  };

  if (!praksissteder) {
    return <></>;
  }

  return (
    <TableRow className={css.editRow}>
      <TableCellTd>
        <PraksisstedSelect
          setSelectedAddPraksisstedId={setSelectedAddPraksisstedId}
          praksissteder={praksissteder}
        />
      </TableCellTd>
      <TableCellTd>
        <RolleSelect
          selectedAddRolle={selectedAddRolle}
          setSelectedAddRolle={setSelectedAddRolle}
        />
      </TableCellTd>
      <TableCellTd align="right">
        <Button.Secondary
          onClick={save}
          disabled={!selectedAddRolle || !selectedAddPraksisstedId}
        >
          {labels.lagre}
        </Button.Secondary>
      </TableCellTd>
    </TableRow>
  );
};

const PraksisstedSelect: React.FC<
  React.PropsWithChildren<{
    setSelectedAddPraksisstedId: (id: string) => void;
    praksissteder: Praksissted[];
  }>
> = ({ setSelectedAddPraksisstedId, praksissteder }) => {
  const { formatMessage } = useIntl();
  const labels = {
    velgTilgangsnivaa: formatMessage({ id: translations.velgTilgangsnivaa.id }),
  };

  return (
    <div>
      <Autocomplete
        id={'autocomplete'}
        ariaLabel={'practiceplace-access-autocomplete'}
        options={praksissteder.map((praksissted) => ({
          ...praksissted,
          onHoverType: StedType.PRAKSISSTED,
        }))}
        onChange={(event: object, value: MuligPraksisstedDto) =>
          setSelectedAddPraksisstedId(value ? value.id : '')
        }
        placeholder={labels.velgTilgangsnivaa}
        renderOption={(option) => (
          <IndentMuligPraksissted praksissted={option} />
        )}
      />
    </div>
  );
};

const RolleSelect: React.FC<
  React.PropsWithChildren<{
    selectedAddRolle: string;
    setSelectedAddRolle: (rolle: Brukerroller) => void;
  }>
> = ({ selectedAddRolle, setSelectedAddRolle }) => {
  const { formatMessage } = useIntl();
  const labels = {
    velgRolle: formatMessage({ id: translations.velgRolle.id }),
  };

  const translateRolle = (rolle: BrukerrollerDto) => {
    return formatMessage({ id: translations[`rolle${rolle}`].id });
  };

  return (
    <div className={css.rolleSelect}>
      <Dropdown
        id={'rolle-select'}
        ariaLabel={'role-select-dropdown'}
        onChange={(event, value) =>
          setSelectedAddRolle(value.id as Brukerroller)
        }
        options={Object.keys(BrukerrollerDto).map((brukerrolle) => ({
          id: brukerrolle,
          navn: translateRolle(brukerrolle as BrukerrollerDto),
        }))}
        placeholder={labels.velgRolle}
        defaultValue={{
          id: selectedAddRolle,
          navn:
            selectedAddRolle &&
            translateRolle(selectedAddRolle as BrukerrollerDto),
        }}
      />
    </div>
  );
};

export default Tilganger;
